// useStatementsData.ts — Sprint 13. Owns the Statements page's
// store-bound surface plus the initial load effect. The orchestrator
// no longer touches useCondoStore directly for any statements
// mutation; it consumes the actions exposed here.
import { useCallback, useEffect, useState } from 'react';
import { staticApi } from '../../utils/staticApi';
import { useCondoStore } from '../../store/condoStore';
import { generateStatementPDF } from './generateStatementPDF';

export interface UseStatementsDataArgs {
  condoId?:   string;
  /** Display name used in the PDF header. Falls back to a generic
   *  string in the hook so the page always gets a printable PDF. */
  condoName?: string;
}

export interface UseStatementsData {
  statements: any[];
  owners:     any[];
  fees:       any[];

  /**
   * Sprint 140 (R13-1, ADR-135) — was `generateForPeriod`, which called a
   * client-side statement generator. Statements are derived server-side from
   * fees, so there was never anything to generate; refetching is the honest
   * action behind that button.
   */
  refresh: () => Promise<void>;
  /** Bulk-send all draft statements for the given period; `null` = every
   *  draft in the condo (Sprint 146 — the "All" filter mode). */
  sendBulk:          (period: string | null) => Promise<void>;
  /** Fire one email by statement id. */
  sendSingle:        (statementId: string) => Promise<void>;
  /** Resolve owner from id, build the PDF, trigger download, toast. */
  downloadPDF:       (statement: any) => Promise<void>;

  /** Toggle for the bulk-send button while the request is in flight. */
  isSending: boolean;
}

export function useStatementsData({
  condoId, condoName,
}: UseStatementsDataArgs): UseStatementsData {
  const {
    statements, owners, fees,
    sendBulkStatements,
    fetchStatements, fetchOwners, fetchFees,
  } = useCondoStore();

  const [isSending, setIsSending] = useState(false);

  // Sprint 34 — finding G1/G2 audit pass. The else-branch used to fall
  // through to UN-scoped fetches when no condoId was passed, which
  // pulled in cross-tenant owners/fees and made the Statements
  // header counts read as the union across condos. Skip the fetch
  // entirely when no condo is selected — same shape as Owners.tsx
  // and StatusCertificates.tsx after their Sprint 34 fixes.
  useEffect(() => {
    if (condoId) {
      fetchStatements(condoId);
      fetchOwners(condoId);
      fetchFees(condoId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condoId]);

  // Sprint 140 (R13-1) — refetch the server's derived statements. The old
  // `generateForPeriod` appended client-computed rows on top of these, which
  // duplicated every owner and understated the balance (ADR-135).
  const refresh = useCallback(async () => {
    if (!condoId) return;
    await Promise.all([
      fetchStatements(condoId),
      fetchOwners(condoId),
      fetchFees(condoId),
    ]);
    staticApi.message.success('Statements refreshed');
  }, [condoId, fetchStatements, fetchOwners, fetchFees]);

  const sendBulk = useCallback(async (period: string | null) => {
    setIsSending(true);
    try {
      const results = await sendBulkStatements(period, condoId);
      const ok = results.filter((r: any) => r.success).length;
      staticApi.message.success(`Sent ${ok} of ${results.length} statement email(s)`);
    } catch {
      staticApi.message.error('Failed to send some emails');
    } finally {
      setIsSending(false);
    }
  }, [sendBulkStatements, condoId]);

  const sendSingle = useCallback(async (statementId: string) => {
    try {
      await useCondoStore.getState().sendStatementEmail(statementId);
      staticApi.message.success('Email sent');
    } catch {
      staticApi.message.error('Failed to send email');
    }
  }, []);

  const downloadPDF = useCallback(async (statement: any) => {
    const owner = owners.find((o: any) =>
      o.id === statement.ownerId || o.userId === statement.ownerId,
    );
    // Sprint 146 (R14-4) — the fee list is no longer passed in: the PDF renders
    // `statement.fees`, which the server populates. See generateStatementPDF.
    await generateStatementPDF(
      statement, owner,
      condoName || 'Condominium Corporation',
    );
    staticApi.message.success('Statement downloaded');
  }, [owners, condoName]);

  return {
    statements, owners, fees,
    refresh, sendBulk, sendSingle, downloadPDF,
    isSending,
  };
}
